import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { TalentCard } from "@/components/TalentCard";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const LOCATIONS = ["", "Montréal"];

const selectCls =
  "border border-white/15 bg-black/40 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white outline-none transition-colors focus:border-gold appearance-none";

export default function Discover() {
  const { t } = useLanguage();
  useSEO(t("discover.eyebrow") + " — Diamotims Agency", t("discover.intro"));
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [language, setLanguage] = useState("");
  const [location, setLocation] = useState("");
  const [skill, setSkill] = useState("");

  useEffect(() => {
    api.get("/talent").then((r) => setAll(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const languages = useMemo(() => {
    const set = new Set();
    all.forEach((x) => (x.languages || []).forEach((l) => set.add(l)));
    return ["", ...Array.from(set).sort()];
  }, [all]);

  const filtered = useMemo(() => {
    const needle = (v, f) => (v || "").toLowerCase().includes(f.toLowerCase());
    return all.filter((x) => {
      if (language && !(x.languages || []).some((l) => needle(l, language))) return false;
      if (location && !needle(x.location, location)) return false;
      if (skill && !(x.skills || []).some((s) => needle(s, skill))) return false;
      if (q) {
        const hay = [x.name, x.bio, ...(x.categories || []), ...(x.credits || [])].join(" ").toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [all, q, language, location, skill]);

  return (
    <main data-testid="discover-page" className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 pb-24 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("discover.eyebrow")}</p>
        <h1 className="mt-4 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.1}>{t("discover.title1")}</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic text-gold">{t("discover.title2")}</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-2xl font-display text-xl font-light italic leading-relaxed text-white/75 sm:text-2xl">
            {t("discover.intro")}
          </p>
        </FadeUp>

        <FadeUp delay={0.2} className="mt-12 border-y border-white/10 py-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="relative lg:col-span-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
              <input
                data-testid="discover-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("discover.search")}
                className="w-full border border-white/15 bg-black/40 py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
              />
            </label>
            <select data-testid="discover-filter-language" value={language} onChange={(e) => setLanguage(e.target.value)} className={selectCls}>
              {languages.map((l) => (
                <option key={l} value={l} className="bg-[#121212]">{l || t("discover.anyLanguage")}</option>
              ))}
            </select>
            <select data-testid="discover-filter-location" value={location} onChange={(e) => setLocation(e.target.value)} className={selectCls}>
              {LOCATIONS.map((l) => (
                <option key={l} value={l} className="bg-[#121212]">{l || t("discover.anyLocation")}</option>
              ))}
            </select>
            <input
              data-testid="discover-filter-skill"
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              placeholder={t("discover.skill")}
              className="border border-white/15 bg-black/40 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
            />
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span data-testid="discover-results-count" className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              {filtered.length} {filtered.length === 1 ? t("discover.match") : t("discover.matches")}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
              {t("discover.privacyNote")}
            </span>
          </div>
        </FadeUp>

        {loading ? (
          <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-white/40">{t("dir.loading")}</div>
        ) : filtered.length === 0 ? (
          <div data-testid="discover-empty" className="py-32 text-center font-display text-2xl font-light italic text-white/60">
            {t("discover.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 pt-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 xl:grid-cols-4">
            {filtered.map((talentItem, i) => (
              <TalentCard key={talentItem.id} talent={talentItem} index={i} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
